import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { processChartText } from '../services/chartProcessingService';
import { AddVideoDetailsDialog } from './AddVideoDetailsDialog';
import { normalizeSongArtistKey } from '../utils/stringNormalization';
import { useThemeColors } from '../theme/useThemeColors';

interface VideoManagerData {
  song?: string;
  artist?: string;
  youtubeLink?: string;
  voiceOverText?: string;
}

// Keep the Video interface simple for the new prop
interface Video {
  song: string;
  artist: string;
  youtubeLink?: string;
}

// Interface for the AddVideoDetailsDialog's expected video structure
interface VideoDetails {
    song: string;
    artist: string;
    thisWeek: number;
    lastWeek: number | string;
    weeks: number;
    peak: number;
    youtubeLink: string;
}

interface ChartUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (chartText: string) => Promise<void>;
  videoData: { [key: string]: VideoManagerData };
  onDetailsSave: (videos: Video[], chartText: string) => Promise<void>;
  onComplete?: (missingCount: number, videos?: Video[]) => void; // Called after saving, with count and video details
}

export const ChartUploadDialog: React.FC<ChartUploadDialogProps> = ({
  open,
  onClose,
  onUpload,
  videoData,
  onDetailsSave,
  onComplete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { themeColor, themeHoverColor } = useThemeColors();
  const [chartText, setChartText] = useState('');
  const [showAddVideoDetails, setShowAddVideoDetails] = useState(false);
  // State should hold the detailed structure needed by AddVideoDetailsDialog
  const [missingVideos, setMissingVideos] = useState<VideoDetails[]>([]); 

  const handleUploadClick = async () => {
    try {
      const entries = await processChartText(chartText);
      
      const normalizedVideoData = Object.entries(videoData).reduce((acc, [key, value]) => {
        const [song, artist] = key.split(' - ');
        const normalizedKey = normalizeSongArtistKey(song, artist);
        acc[normalizedKey] = value;
        return acc;
      }, {} as { [key: string]: VideoManagerData });
      
      const missing = entries.filter(entry => {
        const normalizedKey = normalizeSongArtistKey(entry.song, entry.artist);
        return !normalizedVideoData[normalizedKey];
      });

      if (missing.length > 0) {
        console.log('[ChartUploadDialog] Missing videos based on normalized key:', missing);

        // Map to the DETAILED structure needed by AddVideoDetailsDialog
        const videoDetails: VideoDetails[] = missing.map(entry => {
          const normalizedKey = normalizeSongArtistKey(entry.song, entry.artist);
          const existingData = Object.values(videoData).find(vd => normalizeSongArtistKey(vd.song || '', vd.artist || '') === normalizedKey);

          console.log(`[ChartUploadDialog] Processing missing entry: ${entry.song} - ${entry.artist} (Normalized: ${normalizedKey})`);
          if (existingData) {
            console.log(`  -> Found existing data via normalization: ${existingData.song} - ${existingData.artist}. Using these names.`);
            return {
              song: existingData.song || entry.song,
              artist: existingData.artist || entry.artist,
              youtubeLink: existingData.youtubeLink || '',
              thisWeek: entry.position,
              lastWeek: entry.lastWeek,
              weeks: entry.weeks,
              peak: entry.peak,
            };
          } else {
            console.log(`  -> No existing data found even after normalization check. Using raw parsed names.`);
            return {
              song: entry.song,
              artist: entry.artist,
              youtubeLink: '',
              thisWeek: entry.position,
              lastWeek: entry.lastWeek,
              weeks: entry.weeks,
              peak: entry.peak,
            };
          }
        });

        console.log('[ChartUploadDialog] Video details being sent to AddVideoDetailsDialog:', videoDetails);
        setMissingVideos(videoDetails); // Set state with the detailed VideoDetails[] type
        setShowAddVideoDetails(true);
      } else {
        // No missing videos - upload chart and stay on charts page
        await onUpload(chartText);
        onClose();
        if (onComplete) {
          onComplete(0); // 0 missing videos
        }
      }
    } catch (error) {
      console.error('Error processing chart:', error);
      // Optionally show error to user
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Upload Chart</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Chart Text"
              multiline
              rows={10}
              value={chartText}
              onChange={(e) => setChartText(e.target.value)}
              fullWidth
              placeholder="Paste chart text here..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            sx={{ color: '#000000' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadClick}
            variant="contained"
            sx={{
              backgroundColor: themeColor,
              color: '#000000',
              '&:hover': {
                backgroundColor: themeHoverColor,
              },
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      <AddVideoDetailsDialog
        open={showAddVideoDetails}
        onClose={() => {
           setShowAddVideoDetails(false);
        }}
        onSave={async (editedVideoDetails) => {
          console.log('[ChartUploadDialog] Starting save process...');

          const videosToSave: Video[] = editedVideoDetails.map(details => ({
            song: details.song,
            artist: details.artist,
            youtubeLink: details.youtubeLink,
          }));

          // Fire off the save/chart processing in background - don't await
          // Downloads are triggered IMMEDIATELY at start of handleNewVideosSave,
          // so Import Manager will have data to show right away
          console.log('[ChartUploadDialog] Firing off background save (downloads trigger immediately)...');
          onDetailsSave(videosToSave, chartText)
            .then(() => {
              console.log('[ChartUploadDialog] Background save completed successfully');
            })
            .catch((error) => {
              console.error('[ChartUploadDialog] Background save failed:', error);
              enqueueSnackbar(
                `Background save failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                { variant: 'error' }
              );
            });

          // Close dialogs immediately
          setShowAddVideoDetails(false);
          onClose();

          // Navigate based on number of videos - don't wait for sheet operations
          if (onComplete) {
            console.log(`[ChartUploadDialog] Calling onComplete with ${videosToSave.length} videos...`);
            onComplete(videosToSave.length, videosToSave);
          }
        }}
        videos={missingVideos} 
      />
    </>
  );
}; 